import { FieldExpression, NodeExpression, toString as luceneToString } from 'lucene';

import { Batch, OrderBatch } from '../../models/batch';

export interface GroupedOrderBatches {
	blockedBatches: OrderBatch[];
	transitBatches: OrderBatch[];
	consignmentBatches: OrderBatch[];
	deliveredBatches: OrderBatch[];
}

interface PreparedOrderCardBatches {
	readyToShipBatches: Batch[];
	calledBatches: Batch[];
	callableBatchesCount: number;
	groupedOrderBatches: GroupedOrderBatches;
	allOrderBatches: OrderBatch[];
}

export function createOrderBatchesFilter(supplierReference: string, supplierReferenceItem: string): string {
	const supplierOrderNumberField: FieldExpression = {
		field: 'supplierOrderNumber',
		term: supplierReference,
		quoted: true,
	};
	const supplierOrderItemNumberField: FieldExpression = {
		field: 'supplierOrderItemNumber',
		term: supplierReferenceItem,
		quoted: true,
	};
	const deliveredStatusGroupField: FieldExpression = {
		field: 'statusGroup',
		term: '5',
		quoted: true,
	};

	const orderFilterNode: NodeExpression = {
		left: supplierOrderNumberField,
		operator: 'AND',
		right: supplierOrderItemNumberField,
		parenthesized: true,
	};
	const excludeDeliveredNode: NodeExpression = {
		left: orderFilterNode,
		operator: 'AND NOT',
		right: deliveredStatusGroupField,
		parenthesized: true,
	};

	return luceneToString(excludeDeliveredNode);
}

function mapBatchToOrderBatch(batch: Batch): OrderBatch {
	const shipments = batch.transportReference || batch.transportMode || batch.transportModeDescription
		? [
			{ transportReference: batch.transportReference,
				transportMode: batch.transportMode,
				transportModeDescription: batch.transportModeDescription }
		]
		: [];

	return {
		customerOrderNumber: batch.customerOrderNumber,
		customerOrderItemNumber: batch.customerOrderItemNumber,
		supplierOrderNumber: batch.supplierOrderNumber,
		supplierOrderItemNumber: batch.supplierOrderItemNumber,
		supplierBatchReference: batch.supplierBatchReference ?? '',
		vendorBatchReference: '',
		batchReference: batch.batchReference,
		mill: batch.mill,
		packageSerialNumber: batch.packageSerialNumber,
		millOrderNumber: batch.millOrderNumber,
		millOrderItemNumber: batch.millOrderItemNumber,
		batchStatus: batch.status,
		batchStatusDescription: batch.statusDescription,
		batchStatusGroup: batch.statusGroup,
		qtyBatchNet: batch.qtyBatchNet,
		qtyBatchGross: batch.qtyBatchGross,
		thickness: batch.thickness,
		length: batch.length,
		width: batch.width,
		heatNumber: batch.heatNumber,
		readyToShipDate: batch.readyToShipDate,
		availableInStockDate: String(batch.availableInStockDate ?? ''),
		daysInStock: batch.daysInStock,
		plannedShipmentDate: batch.plannedShipmentDate,
		currentLocation: batch.currentLocation,
		currentLocationDescription: batch.currentLocationDescription,
		supplierShipmentReference: batch.supplierShipmentReference,
		supplierShipmentReferenceItem: batch.supplierShipmentReferenceItem,
		dispatchAdviceNumber: batch.lastDispatchAdviceNumber,
		dispatchAdviceDate: batch.lastDispatchAdviceDate,
		destination: batch.destination,
		destinationDescription: batch.destinationDescription,
		shipments,
		sapBatchId: batch.id
	};
}

export function prepareOrderCardBatches(allBatches: Batch[], hasPcallService: boolean): PreparedOrderCardBatches {
	const statusGroupTwoBatches = allBatches.filter(batch => batch.statusGroup === '2');
	const callableBatchesCount = hasPcallService
		? statusGroupTwoBatches.filter(batch => batch.callable === 'O').length
		: 0;
	const readyToShipBatches = hasPcallService
		? statusGroupTwoBatches.filter(batch => batch.callable !== 'C')
		: statusGroupTwoBatches;
	const calledBatches = hasPcallService
		? statusGroupTwoBatches.filter(batch => batch.callable === 'C')
		: [];

	const allOrderBatches = allBatches
		.filter(batch => batch.statusGroup !== '2')
		.map(batch => mapBatchToOrderBatch(batch));

	return {
		readyToShipBatches,
		calledBatches,
		callableBatchesCount,
		groupedOrderBatches: {
			blockedBatches: allOrderBatches.filter(batch => batch.batchStatusGroup === '1'),
			transitBatches: allOrderBatches.filter(batch => batch.batchStatusGroup === '3'),
			consignmentBatches: allOrderBatches.filter(batch => batch.batchStatusGroup === '4'),
			deliveredBatches: allOrderBatches.filter(batch => batch.batchStatusGroup === '5')
		},
		allOrderBatches
	};
}
